import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ExternalLink, FolderOpen, Send, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const GROUPS = {
  "In Flight": ["Pending", "In Progress", "Changes Requested"],
  "Submitted for Review": ["Review"],
  "Approved": ["Approved"],
};

export default function EditorPortal() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [active, setActive] = useState(null);
  const [finalUrl, setFinalUrl] = useState("");

  const load = async () => {
    const [t, c] = await Promise.all([api.get("/tasks"), api.get("/clients")]);
    // Editors already see only their own tasks — admins in editor view need self-filter
    const my = t.data.filter((x) => x.editor_id === user.user_id);
    setTasks(my);
    setClients(c.data);
  };
  useEffect(() => { load(); }, [user]);

  const cliName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const start = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: "In Progress" });
    toast.success("Moved to In Progress");
    load();
  };

  const submit = async () => {
    if (!finalUrl.trim()) { toast.error("Paste the final Drive link"); return; }
    await api.post(`/tasks/${active.id}/submit`, { final_output_url: finalUrl });
    toast.success("Submitted for approval", {
      description: "Auto-filed into the client's 03_Final_Outputs folder.",
    });
    setActive(null); setFinalUrl(""); load();
  };

  const isEmpty = tasks.length === 0;

  return (
    <div className="p-8 lg:p-12">
      <PageHeader
        eyebrow={`Signed in as ${user?.name}`}
        title="Your Cutting Room"
        subtitle="Just the reels assigned to you. Open the raw footage, submit the final cut, and move on."
      />

      {isEmpty && (
        <div className="rounded-xl border border-white/10 bg-zinc-900 p-10 text-center text-zinc-500">
          Nothing on your desk right now. Enjoy the quiet.
        </div>
      )}

      <div className="space-y-10">
        {Object.entries(GROUPS).map(([label, statuses]) => {
          const items = tasks.filter((t) => statuses.includes(t.status));
          if (items.length === 0) return null;
          return (
            <section key={label} data-testid={`portal-section-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl font-bold">{label}</h2>
                <span className="font-mono text-xs text-zinc-500">{items.length}</span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {items.map((t) => (
                  <div
                    key={t.id}
                    data-testid={`portal-task-${t.id}`}
                    className="rounded-xl border border-white/10 bg-zinc-900 p-6"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                          {cliName(t.client_id)}
                        </div>
                        <h3 className="font-display text-xl font-bold mt-1 leading-tight">{t.title}</h3>
                        <div className="mt-3 flex items-center gap-2">
                          <StatusBadge value={t.status} />
                          <StatusBadge value={t.priority} showDot={false} />
                          <span className="text-[10px] font-mono text-zinc-500">Due {t.deadline}</span>
                        </div>
                      </div>
                    </div>

                    {t.feedback_notes && (
                      <div className="mt-4 rounded-lg border border-rose-500/20 bg-rose-500/5 p-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-300 mb-1">
                          Client feedback
                        </div>
                        <div className="text-sm text-zinc-200">{t.feedback_notes}</div>
                      </div>
                    )}

                    <div className="mt-5 flex flex-wrap gap-2">
                      <a
                        data-testid={`open-raw-${t.id}`}
                        href={t.raw_asset_url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-200 hover:bg-white/5"
                      >
                        <FolderOpen className="h-3.5 w-3.5" /> Open Raw Assets
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      {t.status === "Pending" && (
                        <button
                          data-testid={`start-task-${t.id}`}
                          onClick={() => start(t)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs hover:bg-white/15"
                        >
                          <PlayCircle className="h-3.5 w-3.5" /> Start editing
                        </button>
                      )}
                      {["In Progress", "Changes Requested", "Pending"].includes(t.status) && (
                        <button
                          data-testid={`submit-task-open-${t.id}`}
                          onClick={() => { setActive(t); setFinalUrl(t.final_output_url || ""); }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-white text-zinc-950 px-3 py-1.5 text-xs font-semibold hover:bg-zinc-200"
                        >
                          <Send className="h-3.5 w-3.5" /> Submit for approval
                        </button>
                      )}
                      {t.final_output_url && (
                        <a
                          href={t.final_output_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-sky-300 hover:bg-white/5 font-mono truncate max-w-full"
                        >
                          Final cut ↗
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) { setActive(null); setFinalUrl(""); } }}>
        <DialogContent data-testid="submit-dialog" className="bg-zinc-950 border-white/10 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Submit final cut</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4 pt-2">
              <div className="text-sm text-zinc-400">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {cliName(active.client_id)}
                </div>
                <div className="mt-1 font-medium text-zinc-100">{active.title}</div>
              </div>
              <div>
                <label className="text-sm text-zinc-300">Final Drive link</label>
                <Input
                  data-testid="final-url-input"
                  value={finalUrl}
                  onChange={(e) => setFinalUrl(e.target.value)}
                  placeholder="https://drive.google.com/file/d/..."
                  className="mt-1.5 bg-zinc-900 border-white/10 font-mono text-xs"
                />
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  Will be auto-filed into <span className="font-mono">03_Final_Outputs</span> and sent to
                  the approval queue.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setActive(null); setFinalUrl(""); }}>Cancel</Button>
            <Button
              data-testid="confirm-submit-btn"
              onClick={submit}
              className="bg-white text-zinc-950 hover:bg-zinc-200 rounded-full"
            >
              Submit for approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
